import { parseMoment } from './parseMoment';

const RELATIVE_DATE_RULE_PREFIX = /^(start_at|stop_at)/;

export function isMeaningfulTemporalRule(value) {
    if (value == null || value === '')
        return false;
    if (Array.isArray(value) && value.length === 0)
        return false;
    if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0)
        return false;
    return true;
}

export function hasTemporalConstraint(rules) {
    const normalizedRules = rules || {};
    return isMeaningfulTemporalRule(normalizedRules.not_before)
        || isMeaningfulTemporalRule(normalizedRules.not_after);
}

/**
 * Derive which pricing constraint panels should be visible from rules.
 *
 * @param {object} rules
 * @param {number} [_all_pricing_roles_length] Unused (kept for call-site compat).
 * @return {string[]}
 */
export function getActivePricingConstraints(rules, _all_pricing_roles_length) {
    const normalized_rules = rules || {};
    const sellers = normalized_rules.only_for_roles;
    const constraints = [];

    if (hasTemporalConstraint(normalized_rules))
        constraints.push('temporal');
    // Present key (including []) = roles override. Missing = inherit.
    if (Array.isArray(sellers))
        constraints.push('roles');
    // Same presence semantics as roles / categories.
    if (Array.isArray(normalized_rules.only_for_salepoints))
        constraints.push('salepoints');
    if (Array.isArray(normalized_rules.only_for_countries))
        constraints.push('countries');
    if (
        Array.isArray(normalized_rules.only_for_tickettypes)
        || Array.isArray(normalized_rules.exclude_tickettypes)
    )
        constraints.push('tickettypes');
    if (Array.isArray(normalized_rules.hides_pricings_while_available))
        constraints.push('hides_pricings');
    // Present key (including []) means categories are constrained; missing = all.
    if (Array.isArray(normalized_rules.only_for_categories))
        constraints.push('categories');
    if (normalized_rules.external_voucher)
        constraints.push('external_voucher');

    return [...new Set(constraints)];
}

function formatTemporalRuleValue(value) {
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (RELATIVE_DATE_RULE_PREFIX.test(trimmed))
            return trimmed;
    }

    const parsed = parseMoment(value);
    if (parsed)
        return parsed.format('lll');
    return String(value);
}

export function temporalConstraintSummary(rules, t) {
    if (!hasTemporalConstraint(rules))
        return '';

    const normalizedRules = rules || {};
    const details = [];

    if (isMeaningfulTemporalRule(normalizedRules.not_before)) {
        details.push(`${t('pricinglists.pricing.not_before')} ${formatTemporalRuleValue(normalizedRules.not_before)}`);
    }
    if (isMeaningfulTemporalRule(normalizedRules.not_after)) {
        details.push(`${t('pricinglists.pricing.not_after')} ${formatTemporalRuleValue(normalizedRules.not_after)}`);
    }

    if (details.length === 0)
        return '';

    return details.join(' — ');
}

export function hidesPricingsConstraintSummary(rules, t, pricing_names = {}) {
    const ids = rules?.hides_pricings_while_available;
    if (!Array.isArray(ids) || ids.length === 0)
        return '';
    const labels = ids.map(id => pricing_names[id] || id);
    return `${t('pricinglists.pricing.constraint_hides_pricings')}: ${labels.join(', ')}`;
}

export function rolesConstraintSummary(sellers, t, _all_pricing_roles_length) {
    if (!Array.isArray(sellers))
        return '';
    if (sellers.length === 0)
        return `${t('pricinglists.pricing.constraint_roles')}: —`;
    return `${t('pricinglists.pricing.constraint_roles')}: ${sellers.join(', ')}`;
}

/**
 * @param {string[]|undefined|null} salepoints
 * @param {function} t
 * @return {string}
 */
export function salepointsConstraintSummary(salepoints, t) {
    if (!Array.isArray(salepoints))
        return '';
    if (salepoints.length === 0)
        return `${t('pricinglists.pricing.constraint_salepoints')}: —`;
    return `${t('pricinglists.pricing.constraint_salepoints')}: ${salepoints.join(', ')}`;
}

/**
 * @param {object} rules
 * @param {function} t
 * @return {string}
 */
export function tickettypesConstraintSummary(rules, t) {
    const only = rules?.only_for_tickettypes;
    const exclude = rules?.exclude_tickettypes;
    if (!Array.isArray(only) && !Array.isArray(exclude))
        return '';
    const parts = [];
    if (Array.isArray(only) && only.length > 0)
        parts.push(`+${only.join(', ')}`);
    if (Array.isArray(exclude) && exclude.length > 0)
        parts.push(`-${exclude.join(', ')}`);
    const detail = parts.length > 0 ? parts.join(' ') : '—';
    return `${t('pricinglists.pricing.constraint_tickettypes')}: ${detail}`;
}

/**
 * Drop constraint keys from a rules object.
 *
 * redux-form refuses change(field, undefined) when the field has an initial
 * value (shouldDelete), so callers must replace the parent rules object with
 * the omitted result instead of clearing leaf paths to undefined.
 *
 * Pricing roles are optional: clearing the roles constraint deletes the key
 * (inherit tickettype / unrestricted). Empty [] stays a valid "nobody" value
 * when the constraint panel is active.
 *
 * @param {object} rules
 * @param {string} constraintId
 * @return {object}
 */
export function omit_constraint_keys(rules, constraintId) {
    const next = { ...(rules || {}) };
    if (constraintId === 'temporal') {
        delete next.not_before;
        delete next.not_after;
    } else if (constraintId === 'roles') {
        delete next.only_for_roles;
    } else if (constraintId === 'salepoints') {
        delete next.only_for_salepoints;
    } else if (constraintId === 'countries') {
        delete next.only_for_countries;
    } else if (constraintId === 'tickettypes') {
        delete next.only_for_tickettypes;
        delete next.exclude_tickettypes;
    } else if (constraintId === 'categories') {
        delete next.only_for_categories;
    } else if (constraintId === 'hides_pricings') {
        delete next.hides_pricings_while_available;
    } else if (constraintId === 'external_voucher') {
        delete next.external_voucher;
    }
    return next;
}
